<template>
  <v-container>
    <v-row class="mx-0 justify-space-between my-4 product-card flex-nowrap" style="background-color: #fbfbfb">
      <div class="d-flex flex-column product-card_product-info ml-7 my-3">
        <h3 class="f14 gray0 lato-bold uppercase mb-4">General info</h3>
        <dl class="d-flex align-center mb-1">
          <dt class="f14 lato-bold font-weight-bold miw70 mr-1">Placed on:</dt>
          <dd class="f14">{{ order.date | date(dateOptions) }}</dd>
        </dl>
        <dl class="d-flex align-center mb-1" v-if="order.status">
          <dt class="f14 lato-bold font-weight-bold miw70 mr-1">Status:</dt>
          <dd class="f14">
            {{ order.status }}

            <icons-question
              v-if="showTooltip"
              :width="18"
              :height="18"
              color="#62646A"
              style="min-width: 18px"
              v-tooltip="{
                content: tooltipText,
                classes: 'order-status-tooltip',
                container: false,
                offset: 4
              }"
            />
          </dd>
        </dl>

        <dl v-if="orderPayment.length" class="d-flex align-start mb-1">
          <dt class="f14 lato-bold font-weight-bold miw70 mr-1">Paid by:</dt>
          <dd class="f14">
            <div
              v-for="payment in orderPayment"
              :key="payment.method"
              class="d-flex flex-row flex-nowrap justify-space-between"
            >
              <div>{{ payment.method }}:</div>
              <div class="ml-2">{{ payment.amount | priceFilter }}</div>
            </div>
          </dd>
        </dl>

        <dl class="d-flex align-center mb-1" v-if="order.shipping_status">
          <dt class="f14 lato-bold font-weight-bold miw70 mr-1">Shipment:</dt>
          <dd class="f14">{{ order.shipping_status }}</dd>
        </dl>
        <div class="mt-5">
          <popper
            v-if="order.recipient && order.recipient.gift_message"
            :options="{
              placement: 'top',
              modifiers: {
                offset: {
                  offset: '0,10px'
                },
                preventOverflow: {
                  boundariesElement: 'offsetParent'
                }
              }
            }"
          >
            <div class="popper text-left px-4 py-4">
              {{ order.recipient.gift_message }}
            </div>
            <div
              class="mt-auto product-card_product-info_greet-msg d-flex"
              slot="reference"
            >
              <v-img
                src='/images/greetings_small1.svg'
                max-width="15px"
                max-height="20px"
                class="mr-2"
              ></v-img>
              <span class="product-card_product-info_greet-msg_btn">Gift message</span>
            </div>
          </popper>
        </div>
      </div>
      <div class="d-flex flex-column mr-3 my-3 product-card_qty" v-if="order.customer">
        <h3 class="f14 gray0 lato-bold uppercase mb-4">Sender</h3>
        <address-info :address="order.customer" />
      </div>
      <div class="d-flex flex-column mr-3 my-3 product-card_qty" v-if="Object.keys(shippingAddress).length">
        <h3 class="f14 gray0 lato-bold uppercase mb-4">Ship to</h3>
        <address-info :address="shippingAddress" />
      </div>
      <div class="d-flex flex-column ml-3 pt-5 pb-4 pl-6 pr-6 cost-information bl1" style="background-color: #fff">
        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.subtotal">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">sub total</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ order.subtotal | priceFilter }}</p>
        </div>
        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.shipping || order.shipping === 0">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">shipping & handling</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ order.shipping | priceFilter }}</p>
        </div>
        <!--
          Estimated customs duty + VAT on shipments to international recipients,
          already part of the order total. Rendered next to shipping because it
          is shipment-driven, and hidden on a falsy check like Shipping
          Surcharges below: domestic orders and orders placed before the feature
          existed both report 0 (MP-3240).
        -->
        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.import_charges">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">Import Charges</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ order.import_charges | priceFilter }}</p>
        </div>
        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.setup">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">Customization setup</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ order.setup | priceFilter }}</p>
        </div>
        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.tax || order.tax === 0">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">tax</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ order.tax | priceFilter }}</p>
        </div>

        <div
          class="d-flex align-start justify-space-between full-width mb-3"
          v-for="(fee, index) in fees"
          :key="index"
        >
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">{{ fee.name }}</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ fee.amount | priceFilter }}</p>
        </div>

        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.shipping_surcharge">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">Shipping Surcharges</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0">{{ order.shipping_surcharge | priceFilter }}</p>
        </div>
        <div class="d-flex align-start justify-space-between full-width mb-3" v-if="order.discount_amount">
          <div class="order-info">
            <dl class="d-flex align-center justify-space-between">
              <dt class="f14 lato-bold font-weight-bold miw70 uppercase">Discount</dt>
              <dd class="f14"></dd>
            </dl>
          </div>
          <p class="f14 lato-bold ma-0" :class="{ red1: order.discount_amount < 0 }">
            {{ order.discount_amount | priceFilter }}
          </p>
        </div>
        <div v-if="coveredPrice" class="general-info__total-price bt-large">
          <div class="general-info__total-row">
            <span class="general-info__total-label">Total:</span>
            <div class="general-info__total-values">
              <span>{{ totalPrice | priceFilter }}</span>
              <span>{{ totalCoveredPrice | priceFilter }}</span>
            </div>
          </div>
          <div class="general-info__contribution-box">
            <span>Company covered</span>
            <span>{{ coveredPrice | priceFilter }}</span>
          </div>
        </div>
        <dl v-else class="d-flex align-center justify-space-between full-width pt-2 mt-auto bt-large">
          <dt class="f16 lato-bold font-weight-bold miw70 uppercase">Total</dt>
          <dd class="f18 lato-bold"> {{ totalPrice | priceFilter }} </dd>
        </dl>
      </div>
    </v-row>
  </v-container>
</template>

<script>
import Popper from 'vue-popperjs'
import AddressInfo from '@/components/myOrders/AddressInfo'

export default {
  name: 'GeneralInfo',
  components: {
    AddressInfo,
    popper: Popper
  },
  props: {
    order: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data: () => ({
    dateOptions: {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    },
    shippingAddress: {}
  }),
  computed: {
    fees () { return this.order?.fees ?? [] },
    orderPayment () {
      return this.order?.payment_methods ?? []
    },
    orderTooltip () {
      return this.order?.status_tooltip ?? null
    },
    showTooltip () {
      return !!this.orderTooltip
    },
    tooltipText () {
      return this.orderTooltip
    },
    totalPrice () {
      return this.order?.total ?? 0
    },
    coveredPrice () {
      return this.order?.totalCovered ?? null
    },
    totalCoveredPrice () {
      return this.totalPrice - this.coveredPrice
    },
  },
  created () {
    this.shippingAddress = this.order.recipient?.shipping_address && Object.keys(this.order.recipient?.shipping_address).length
      ? this.order.recipient?.shipping_address : {}
  },
  methods: {
    isCanceled (status) {
      return status.toLowerCase() === 'canceled'
    }
  }
}
</script>

<style lang="scss">
.order-status-tooltip {
  background: #fff;
  padding: 14px;
  font-family: 'Lato-Regular', sans-serif;
  max-width: 220px;
  font-size: 14px;
  line-height: 17px;
  color: #16181F;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.15);
}
</style>

<style lang="scss" scoped>
.general-info {
  &__total-price {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 15px;
  }

  &__total-label {
    font-size: 16px;
    font-weight: 700;
    line-height: 133%;
    letter-spacing: 0;
  }

  &__total-values {
    display: flex;
    align-items: center;
    gap: 8px;

    span {
      &:first-child {
        font-size: 14px;
        color: #62646A;
        text-decoration: line-through;
        font-weight: 400;
      }

      &:last-child {
        font-weight: 700;
        font-size: 24px;
        line-height: 100%;
        letter-spacing: 0;
        color: #000000;
      }
    }
  }

  &__contribution-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 8px;
    background-color: #FAFAFA;
    border-radius: 4px;
    font-weight: 700;
    font-size: 14px;
    color: #007D85;
  }
}
</style>
